import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { DatePicker } from "../components/ui/date-picker";
import { 
  ArrowLeft, 
  FileDown, 
  X, 
  User, 
  Calendar,
  Shield, 
  Loader2,
  CheckCircle2,
  ChevronRight,
  Building2,
  Sparkles,
  BadgeCheck,
  Link2,
  Copy,
  ExternalLink
} from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IRDAI Claim Experience Data
const CLAIM_EXPERIENCE_DATA = {
  "hdfc-ergo": {
    claim_settlement_ratio: "~96%",
    incurred_claim_ratio: "84.85%",
    icr_previous_year: "80.98%",
    icr_trend: "Increased",
    network_hospitals: "13,000+",
    cashless_hospitals: "10,000+"
  },
  "care-health": {
    claim_settlement_ratio: "~92%",
    incurred_claim_ratio: "64.53%",
    icr_previous_year: "57.69%",
    icr_trend: "Increased",
    network_hospitals: "24,800+",
    cashless_hospitals: "21,000+"
  },
  "icici-lombard": {
    claim_settlement_ratio: "~94%",
    incurred_claim_ratio: "82.24%",
    icr_previous_year: "78.85%",
    icr_trend: "Increased",
    network_hospitals: "10,800+",
    cashless_hospitals: "9,000+"
  },
  "aditya-birla": {
    claim_settlement_ratio: "~93%",
    incurred_claim_ratio: "71.50%",
    icr_previous_year: "68.31%",
    icr_trend: "Increased",
    network_hospitals: "10,500+",
    cashless_hospitals: "10,000+"
  },
  "manipal-cigna": {
    claim_settlement_ratio: "~93.7%",
    incurred_claim_ratio: "64%",
    icr_previous_year: "65%",
    icr_trend: "Stable",
    network_hospitals: "8,000+",
    cashless_hospitals: "7,500+"
  }
};

// Comparison Categories (same as website)
const COMPARISON_CATEGORIES = [
  {
    id: "claim_experience",
    name: "Claim Experience (IRDAI FY 2024-25)",
    color: [59, 130, 246],
    fields: [
      { key: "claim_settlement_ratio", label: "Claim Settlement Ratio (CSR)" },
      { key: "incurred_claim_ratio", label: "Incurred Claim Ratio (ICR)" },
      { key: "icr_previous_year", label: "ICR Previous Year" },
      { key: "icr_trend", label: "ICR Trend" },
      { key: "network_hospitals", label: "Network Hospitals" },
      { key: "cashless_hospitals", label: "Cashless Hospitals" },
    ]
  },
  {
    id: "basic",
    name: "Basic Policy Details",
    color: [15, 23, 42],
    fields: [
      { key: "sum_insured_display", label: "Sum Insured Options" },
      { key: "room_rent", label: "Room Rent" },
      { key: "icu_limit", label: "ICU Limit" },
      { key: "policy_tenure", label: "Policy Tenure" },
      { key: "family_floater", label: "Family Floater" },
    ]
  },
  {
    id: "hospitalization",
    name: "Hospitalization Coverage",
    color: [34, 197, 94],
    fields: [
      { key: "pre_hospitalization", label: "Pre-Hospitalization" },
      { key: "post_hospitalization", label: "Post-Hospitalization" },
      { key: "day_care", label: "Day Care Procedures" },
      { key: "domiciliary", label: "Domiciliary Treatment" },
      { key: "organ_donor", label: "Organ Donor Coverage" },
    ]
  },
  {
    id: "ambulance",
    name: "Ambulance & Emergency",
    color: [239, 68, 68],
    fields: [
      { key: "road_ambulance", label: "Road Ambulance" },
      { key: "air_ambulance", label: "Air Ambulance" },
    ]
  },
  {
    id: "restore",
    name: "Restore & Bonus Benefits",
    color: [139, 92, 246],
    fields: [
      { key: "restore_benefit", label: "Restore/Recharge Benefit" },
      { key: "ncb", label: "No Claim Bonus (NCB)" },
      { key: "ncb_reduction", label: "NCB Reduction on Claim" },
    ]
  },
  {
    id: "ayush",
    name: "AYUSH & Alternative Treatment",
    color: [34, 139, 34],
    fields: [
      { key: "ayush_coverage", label: "AYUSH Coverage" },
      { key: "ayush_pre_post", label: "AYUSH Pre/Post Hospitalization" },
    ]
  },
  {
    id: "waiting",
    name: "Waiting Periods",
    color: [220, 53, 69],
    fields: [
      { key: "initial_waiting", label: "Initial Waiting Period" },
      { key: "specific_disease_waiting", label: "Specific Disease Waiting" },
      { key: "pre_existing_waiting", label: "Pre-Existing Disease (PED)" },
      { key: "ped_modification", label: "PED Modification Option" },
    ]
  },
  {
    id: "additional",
    name: "Additional Benefits",
    color: [0, 128, 128],
    fields: [
      { key: "health_checkup", label: "Annual Health Checkup" },
      { key: "consumables", label: "Consumables Coverage" },
      { key: "robotics", label: "Robotic Surgery" },
      { key: "second_opinion", label: "Second E-Opinion" },
    ]
  },
  {
    id: "optional",
    name: "Optional Coverages (Riders)",
    color: [139, 69, 19],
    fields: [
      { key: "maternity", label: "Maternity Coverage" },
      { key: "opd", label: "OPD Coverage" },
      { key: "hospital_cash", label: "Hospital Daily Cash" },
      { key: "critical_illness", label: "Critical Illness" },
    ]
  },
  {
    id: "copay",
    name: "Co-payments & Limits",
    color: [255, 140, 0],
    fields: [
      { key: "copay_age", label: "Co-payment (Age Based)" },
      { key: "copay_zone", label: "Zonal Co-payment" },
      { key: "sub_limits", label: "Sub-Limits" },
    ]
  },
  {
    id: "discounts",
    name: "Discounts Available",
    color: [217, 119, 6],
    fields: [
      { key: "multi_year_discount", label: "Multi-Year Discount" },
      { key: "family_discount", label: "Family Discount" },
      { key: "online_discount", label: "Online Purchase Discount" },
    ]
  }
];

// Extract comparison data from plan
const extractComparisonData = (plan) => {
  if (!plan) return {};
  
  const getRoomRent = () => {
    const baseRoomRent = plan.coverage_details?.hospitalization?.room_rent || "Covered up to SI";
    if (plan.id === 'icici-elevate') return `${baseRoomRent} (Any Room available)`;
    return baseRoomRent;
  };
  
  const getAirAmbulance = () => {
    const baseAir = plan.coverage_details?.ambulance?.air || "Not Covered";
    const airRider = plan.riders?.find(r => r.name.toLowerCase().includes('air ambulance'));
    if (airRider) return `${baseAir} (via Rider)`;
    return baseAir;
  };
  
  const getRoadAmbulance = () => plan.coverage_details?.ambulance?.road || "Covered";
  
  const getMaternity = () => {
    const maternityRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('maternity') || r.name.toLowerCase().includes('parenthood')
    );
    if (maternityRider) {
      const waitingPeriod = maternityRider.waiting_period || "24 months";
      return `Optional: ${maternityRider.name} (WP: ${waitingPeriod})`;
    }
    return "Not Covered";
  };
  
  const getOPD = () => {
    const opdRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('opd') || r.name.toLowerCase().includes('wellbeing') || r.name.toLowerCase().includes('befit')
    );
    if (opdRider) return `Optional: ${opdRider.name}`;
    return "Not Covered";
  };
  
  const getConsumables = () => {
    const consumableRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('consumable') || r.name.toLowerCase().includes('claim shield') || r.name.toLowerCase().includes('claim protector')
    );
    if (consumableRider) return `Covered via ${consumableRider.name}`;
    return "Covered up to SI";
  };
  
  const getRestoreBenefit = () => {
    const restoreBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('restore') || b.name.toLowerCase().includes('recharge') || 
      b.name.toLowerCase().includes('reset') || b.name.toLowerCase().includes('secure')
    );
    if (restoreBenefit) {
      const detail = restoreBenefit.details?.[0] || restoreBenefit.description;
      return detail || "Available";
    }
    return "Available";
  };
  
  const getNCB = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative') || b.name.toLowerCase().includes('loyalty')
    );
    if (ncbBenefit) {
      const detail = ncbBenefit.details?.[0] || ncbBenefit.description;
      return detail || "50% per year up to 100%";
    }
    return "50% per year up to 100%";
  };
  
  const getNCBReduction = () => {
    const ncbBenefit = plan.core_benefits?.find(b => 
      b.name.toLowerCase().includes('bonus') || b.name.toLowerCase().includes('cumulative')
    );
    if (ncbBenefit?.details) {
      const reduction = ncbBenefit.details.find(d => 
        d.toLowerCase().includes('reduce') || d.toLowerCase().includes('claim') || d.toLowerCase().includes('maintain')
      );
      return reduction || "May reduce on claim";
    }
    return "May reduce on claim";
  };
  
  const getPEDModification = () => {
    const pedInfo = plan.waiting_periods?.pre_existing?.reduction;
    const pedRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('ped') || r.name.toLowerCase().includes('jumpstart') || r.name.toLowerCase().includes('pre-existing')
    );
    if (pedRider) return `${pedInfo || 'Standard'} (${pedRider.name} available)`;
    return pedInfo || "Standard (36 months)";
  };
  
  const getHealthCheckup = () => {
    if (plan.health_checkup) {
      const limits = plan.health_checkup.limits_individual || plan.health_checkup.limits_floater;
      if (limits) {
        const firstKey = Object.keys(limits)[0];
        return `${limits[firstKey]}`;
      }
      if (plan.health_checkup.availability) return plan.health_checkup.availability;
    }
    return "Covered as per plan";
  };
  
  const getHospitalCash = () => {
    const hcRider = plan.riders?.find(r => 
      r.name.toLowerCase().includes('hospital cash') || r.name.toLowerCase().includes('daily cash')
    );
    if (hcRider) return `Optional: ${hcRider.name}`;
    return "Not Available";
  };
  
  const getCriticalIllness = () => {
    const ciRider = plan.riders?.find(r => r.name.toLowerCase().includes('critical illness'));
    if (ciRider) {
      const limit = ciRider.details?.find(d => d.includes('₹') || d.includes('Lakh'));
      return `Optional: ${ciRider.name}${limit ? ` (${limit})` : ''}`;
    }
    return "Not Covered";
  };
  
  const getAYUSHPrePost = () => {
    const ayushInfo = plan.coverage_details?.ayush;
    if (ayushInfo?.pre_post) return ayushInfo.pre_post;
    return "Inpatient Only";
  };
  
  const getMultiYearDiscount = () => {
    const discount = plan.discounts?.find(d => 
      d.name.toLowerCase().includes('multi') || d.name.toLowerCase().includes('long') || d.name.toLowerCase().includes('tenure')
    );
    if (discount) return discount.description || discount.discount || "7.5% (2yr) / 10% (3yr)";
    return "7.5% (2yr) / 10% (3yr)";
  };
  
  const data = {
    sum_insured_display: plan.sum_insured_options?.slice(0, 4).join(", ") + (plan.sum_insured_options?.length > 4 ? "..." : "") || "N/A",
    room_rent: getRoomRent(),
    icu_limit: plan.coverage_details?.hospitalization?.icu_charges || "Covered up to SI",
    policy_tenure: plan.policy_options?.tenure?.join(", ") || "1, 2, 3 Years",
    family_floater: plan.policy_options?.max_members_floater || "Available",
    pre_hospitalization: plan.coverage_details?.pre_hospitalization?.days || "60 Days",
    post_hospitalization: plan.coverage_details?.post_hospitalization?.days || "180 Days",
    day_care: plan.coverage_details?.day_care?.procedures || "All Covered",
    domiciliary: plan.coverage_details?.domiciliary?.coverage || "Covered up to SI",
    organ_donor: plan.coverage_details?.organ_donor?.coverage || "Covered up to SI",
    road_ambulance: getRoadAmbulance(),
    air_ambulance: getAirAmbulance(),
    restore_benefit: getRestoreBenefit(),
    ncb: getNCB(),
    ncb_reduction: getNCBReduction(),
    ayush_coverage: plan.coverage_details?.ayush?.coverage || "Covered up to SI",
    ayush_pre_post: getAYUSHPrePost(),
    initial_waiting: plan.waiting_periods?.initial?.period || "30 Days",
    specific_disease_waiting: plan.waiting_periods?.specific_diseases?.period || "24 Months",
    pre_existing_waiting: plan.waiting_periods?.pre_existing?.period || "36 Months",
    ped_modification: getPEDModification(),
    health_checkup: getHealthCheckup(),
    consumables: getConsumables(),
    robotics: plan.coverage_details?.advanced_technology?.coverage || plan.coverage_details?.modern_treatments?.coverage || "Covered up to SI",
    second_opinion: "Available",
    maternity: getMaternity(),
    opd: getOPD(),
    hospital_cash: getHospitalCash(),
    critical_illness: getCriticalIllness(),
    copay_age: "No Co-pay",
    copay_zone: "No Zonal Co-pay (Pan India)",
    sub_limits: getRoomRent().includes("No sub-limit") || getRoomRent().includes("No Capping") || getRoomRent().includes("At Actuals") ? "No Sub-limits" : "May have sub-limits",
    multi_year_discount: getMultiYearDiscount(),
    family_discount: plan.discounts?.find(d => d.name.toLowerCase().includes('family'))?.discount || "Available",
    online_discount: plan.discounts?.find(d => d.name.toLowerCase().includes('online'))?.discount || "Available",
  };
  
  const companyId = getCompanyId(plan);
  if (companyId && CLAIM_EXPERIENCE_DATA[companyId]) {
    const claimData = CLAIM_EXPERIENCE_DATA[companyId];
    data.claim_settlement_ratio = claimData.claim_settlement_ratio;
    data.incurred_claim_ratio = claimData.incurred_claim_ratio;
    data.icr_previous_year = claimData.icr_previous_year;
    data.icr_trend = claimData.icr_trend;
    data.network_hospitals = claimData.network_hospitals;
    data.cashless_hospitals = claimData.cashless_hospitals;
  }
  
  return data;
};

const getCompanyId = (plan) => {
  const company = plan.company?.toLowerCase() || "";
  if (company.includes('hdfc')) return 'hdfc-ergo';
  if (company.includes('care')) return 'care-health';
  if (company.includes('icici')) return 'icici-lombard';
  if (company.includes('aditya') || company.includes('birla')) return 'aditya-birla';
  if (company.includes('manipal') || company.includes('cigna')) return 'manipal-cigna';
  return null;
};

// Steps for the wizard
const STEPS = [
  { id: 1, name: "Client", icon: User },
  { id: 2, name: "Plans", icon: Shield },
  { id: 3, name: "Riders", icon: Sparkles },
  { id: 4, name: "Review", icon: FileDown },
];

export default function QuotationGenerator() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  const [clientName, setClientName] = useState("");
  const [clientAge, setClientAge] = useState("");
  const [quoteDate, setQuoteDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [planPremiums, setPlanPremiums] = useState({});
  const [selectedRiders, setSelectedRiders] = useState({});

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_URL}/api/plans`);
        const data = await response.json();
        setPlans(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching plans:", error);
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handlePlanSelect = (planId) => {
    if (selectedPlans.includes(planId)) {
      setSelectedPlans(selectedPlans.filter(id => id !== planId));
      const newPremiums = { ...planPremiums };
      delete newPremiums[planId];
      setPlanPremiums(newPremiums);
      const newRiders = { ...selectedRiders };
      delete newRiders[planId];
      setSelectedRiders(newRiders);
    } else if (selectedPlans.length < 4) {
      setSelectedPlans([...selectedPlans, planId]);
      setPlanPremiums({ ...planPremiums, [planId]: { "25L": "", "50L": "", "1Cr": "" } });
      setSelectedRiders({ ...selectedRiders, [planId]: [] });
    }
  };

  const handlePremiumChange = (planId, tier, value) => {
    setPlanPremiums({
      ...planPremiums,
      [planId]: { ...planPremiums[planId], [tier]: value }
    });
  };

  const handleRiderToggle = (planId, riderId) => {
    const currentRiders = selectedRiders[planId] || [];
    if (currentRiders.includes(riderId)) {
      setSelectedRiders({ ...selectedRiders, [planId]: currentRiders.filter(id => id !== riderId) });
    } else {
      setSelectedRiders({ ...selectedRiders, [planId]: [...currentRiders, riderId] });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const loadImageAsBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const generatePDF = async () => {
    if (!clientName || selectedPlans.length < 2) {
      alert("Please enter client name and select at least 2 plans");
      return;
    }

    setGenerating(true);

    try {
      const planDetails = await Promise.all(
        selectedPlans.map(async (planId) => {
          const response = await fetch(`${API_URL}/api/plans/${planId}`);
          return response.json();
        })
      );

      const logoPromises = planDetails.map(p => loadImageAsBase64(p.company_logo));
      const logos = await Promise.all(logoPromises);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      
      const navy = [15, 23, 42];
      const gold = [217, 119, 6];
      const white = [255, 255, 255];
      const lightGray = [248, 250, 252];
      const borderColor = [226, 232, 240];

      // Header
      doc.setFillColor(...navy);
      doc.rect(0, 0, pageWidth, 28, 'F');
      doc.setFillColor(...gold);
      doc.rect(0, 28, pageWidth, 2, 'F');
      
      doc.setTextColor(...white);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("HEALTH INSURANCE COMPARISON", margin, 12);
      
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Prepared for: ${clientName}${clientAge ? ` (Age: ${clientAge})` : ''}`, margin, 20);
      doc.text(`Date: ${formatDate(quoteDate)}`, margin, 25);
      
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...gold);
      doc.text("Insurance Insights by Prashant K Uunadkat", pageWidth - margin, 12, { align: 'right' });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...white);
      doc.text("Beshak Verified Advisor | +91 9510020907", pageWidth - margin, 20, { align: 'right' });

      // Plan Cards with Logos
      let yPos = 35;
      const cardWidth = (pageWidth - (2 * margin) - ((planDetails.length - 1) * 5)) / planDetails.length;
      const cardHeight = 28;
      
      planDetails.forEach((plan, index) => {
        const xPos = margin + (index * (cardWidth + 5));
        
        doc.setFillColor(240, 245, 250);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'F');
        doc.setDrawColor(...navy);
        doc.setLineWidth(0.3);
        doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 3, 3, 'S');
        
        if (logos[index]) {
          try {
            doc.addImage(logos[index], 'PNG', xPos + 4, yPos + 4, 20, 20);
          } catch (e) {
            doc.setFillColor(...navy);
            doc.rect(xPos + 4, yPos + 4, 20, 20, 'F');
          }
        }
        
        doc.setTextColor(...navy);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        const planName = plan.name.replace("ManipalCigna ", "").replace("HDFC ERGO ", "").replace("Care ", "").replace("ICICI Lombard ", "").replace("Aditya Birla ", "");
        doc.text(planName, xPos + 28, yPos + 12, { maxWidth: cardWidth - 32 });
        
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text(plan.company, xPos + 28, yPos + 19, { maxWidth: cardWidth - 32 });
      });

      yPos += cardHeight + 5;

      const colCount = planDetails.length + 1;
      const labelColWidth = 55;
      const planColWidth = (pageWidth - (2 * margin) - labelColWidth) / planDetails.length;
      const comparisonData = planDetails.map(plan => extractComparisonData(plan));
      
      const body = [];
      
      COMPARISON_CATEGORIES.forEach(category => {
        body.push([{ 
          content: category.name, 
          colSpan: colCount, 
          styles: { 
            fillColor: category.color, 
            textColor: white, 
            fontStyle: 'bold', 
            halign: 'center',
            fontSize: 9,
            cellPadding: 3
          } 
        }]);
        
        category.fields.forEach(field => {
          const row = [field.label];
          comparisonData.forEach(data => {
            row.push(data[field.key] || "N/A");
          });
          body.push(row);
        });
      });

      autoTable(doc, {
        body: body,
        startY: yPos,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          overflow: 'linebreak',
          lineColor: borderColor,
          lineWidth: 0.2,
          valign: 'middle'
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: labelColWidth, fillColor: lightGray, halign: 'left', fontSize: 8 },
          ...Object.fromEntries(
            planDetails.map((_, i) => [i + 1, { cellWidth: planColWidth, halign: 'center', fontSize: 8 }])
          )
        },
        alternateRowStyles: { fillColor: [255, 255, 255] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index > 0) {
            const text = String(data.cell.raw || '');
            if (text.toLowerCase().includes('covered') || text.toLowerCase().includes('available') || 
                text.toLowerCase().includes('unlimited') || text.toLowerCase().includes('no co-pay')) {
              data.cell.styles.textColor = [34, 139, 34];
            }
            if (text.toLowerCase().includes('not covered') || text.toLowerCase().includes('not available')) {
              data.cell.styles.textColor = [200, 50, 50];
            }
          }
        }
      });

      // Riders Section
      let currentY = doc.lastAutoTable?.finalY || 170;
      
      if (currentY > pageHeight - 60) {
        doc.addPage();
        currentY = margin;
      }
      
      currentY += 8;
      
      doc.setFillColor(128, 0, 128);
      doc.rect(margin, currentY, pageWidth - (2 * margin), 8, 'F');
      doc.setTextColor(...white);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("SELECTED RIDERS FOR THIS QUOTE", pageWidth / 2, currentY + 5.5, { align: 'center' });
      currentY += 12;

      const riderRows = [];
      const allRiderNames = new Set();
      planDetails.forEach(p => {
        const planRiderIds = selectedRiders[p.id] || [];
        planRiderIds.forEach(riderId => {
          const rider = p.riders?.find(r => r.id === riderId);
          if (rider) allRiderNames.add(rider.name);
        });
      });
      
      if (allRiderNames.size === 0) {
        riderRows.push(["No riders selected", ...planDetails.map(() => "Base Plan Only")]);
      } else {
        Array.from(allRiderNames).forEach(riderName => {
          const row = [riderName];
          planDetails.forEach(p => {
            const planRiderIds = selectedRiders[p.id] || [];
            const rider = p.riders?.find(r => r.name === riderName && planRiderIds.includes(r.id));
            if (rider) {
              row.push(`✓ ${rider.description || ""}`);
            } else {
              row.push("-");
            }
          });
          riderRows.push(row);
        });
      }

      autoTable(doc, {
        body: riderRows,
        startY: currentY,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          overflow: 'linebreak',
          lineColor: borderColor,
          lineWidth: 0.2,
          valign: 'middle'
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: labelColWidth, fillColor: lightGray, halign: 'left' },
          ...Object.fromEntries(
            planDetails.map((_, i) => [i + 1, { cellWidth: planColWidth, halign: 'center' }])
          )
        },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index > 0) {
            const text = String(data.cell.raw || '');
            if (text.startsWith('✓')) {
              data.cell.styles.textColor = [34, 139, 34];
            }
          }
        }
      });

      // Premium Section
      currentY = doc.lastAutoTable?.finalY || 170;
      
      if (currentY > pageHeight - 50) {
        doc.addPage();
        currentY = margin;
      }
      
      currentY += 8;
      
      doc.setFillColor(...navy);
      doc.rect(margin, currentY, pageWidth - (2 * margin), 8, 'F');
      doc.setTextColor(...white);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("PREMIUM DETAILS (Indicative + 18% GST)", pageWidth / 2, currentY + 5.5, { align: 'center' });
      currentY += 12;

      const premiumRows = [];
      const premiumTiers = ["25L", "50L", "1Cr"];
      premiumTiers.forEach(tier => {
        const hasAnyPremium = planDetails.some(p => planPremiums[p.id]?.[tier]);
        if (hasAnyPremium) {
          const row = [`₹${tier} Sum Insured`];
          planDetails.forEach(p => {
            const premium = planPremiums[p.id]?.[tier];
            row.push(premium ? `₹${Number(premium).toLocaleString('en-IN')}/-` : "-");
          });
          premiumRows.push(row);
        }
      });

      if (premiumRows.length > 0) {
        autoTable(doc, {
          body: premiumRows,
          startY: currentY,
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 10,
            cellPadding: 3,
            overflow: 'linebreak',
            lineColor: borderColor,
            lineWidth: 0.2,
            valign: 'middle',
            fontStyle: 'bold'
          },
          columnStyles: {
            0: { cellWidth: labelColWidth, fillColor: lightGray, halign: 'left' },
            ...Object.fromEntries(
              planDetails.map((_, i) => [i + 1, { cellWidth: planColWidth, halign: 'center', textColor: navy }])
            )
          }
        });
        currentY = doc.lastAutoTable?.finalY || currentY;
      }

      // Hospital Network Links
      currentY += 8;
      
      if (currentY > pageHeight - 40) {
        doc.addPage();
        currentY = margin;
      }
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...navy);
      doc.text("CASHLESS HOSPITAL NETWORK LINKS (Click to view):", margin, currentY);
      currentY += 6;
      
      planDetails.forEach((p) => {
        const url = p.hospital_network?.cashless_network_url;
        if (url) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(50, 50, 50);
          const companyShort = p.company.split(' ')[0];
          doc.text(`${companyShort}: `, margin, currentY);
          
          const labelWidth = doc.getTextWidth(`${companyShort}: `);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(59, 130, 246);
          doc.textWithLink(url.length > 80 ? url.substring(0, 77) + "..." : url, margin + labelWidth, currentY, { url: url });
          currentY += 5;
        }
      });

      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        doc.setFillColor(...lightGray);
        doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.5);
        doc.line(0, pageHeight - 12, pageWidth, pageHeight - 12);
        
        doc.setFontSize(6);
        doc.setTextColor(100, 100, 100);
        doc.setFont("helvetica", "normal");
        doc.text("Disclaimer: Premium is indicative and subject to medical underwriting. GST @18% applicable. This is not a policy document.", margin, pageHeight - 7);
        
        doc.setFontSize(7);
        doc.setTextColor(50, 50, 50);
        doc.text("Contact: +91 9510020907 | prasant@shreefinservices.com", margin, pageHeight - 3);
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...gold);
        doc.text(`Insurance Insights by Prashant K Uunadkat  |  Page ${i}/${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      }

      const fileName = `Quote_${clientName.replace(/\s+/g, '_')}_${formatDate(quoteDate).replace(/\s+/g, '-').replace(/,/g, '')}.pdf`;
      doc.save(fileName);
      setGenerating(false);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(`Error: ${error.message}`);
      setGenerating(false);
    }
  };

  const createShareableLink = async () => {
    if (!clientName || selectedPlans.length < 2) {
      alert("Please enter client name and select at least 2 plans");
      return;
    }

    setCreatingLink(true);
    setShareLink(null);

    try {
      const response = await fetch(`${API_URL}/api/quotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientName,
          client_age: clientAge,
          quote_date: quoteDate,
          selected_plan_ids: selectedPlans,
          plan_premiums: planPremiums,
          selected_riders: selectedRiders
        })
      });

      if (!response.ok) {
        throw new Error("Failed to create quotation");
      }

      const data = await response.json();
      const fullUrl = `${window.location.origin}/quote/${data.id}`;
      setShareLink(fullUrl);
      
    } catch (error) {
      console.error("Error creating shareable link:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setCreatingLink(false);
    }
  };

  const copyShareLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      alert("Link copied to clipboard!");
    }
  };

  const getSelectedPlanData = (planId) => plans.find(p => p.id === planId);

  const canProceed = () => {
    if (currentStep === 1) return clientName.trim().length > 0;
    if (currentStep === 2) return selectedPlans.length >= 2;
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#A87608] animate-spin mx-auto mb-4" />
          <p className="text-[#3A4557] text-lg">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#C9D4E6] bg-[#EDF1F8]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(-1)} 
                className="text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] tracking-tight">
                  Quotation Generator
                </h1>
                <p className="text-[#3A4557] text-sm mt-0.5">Create professional comparison PDFs</p>
              </div>
            </div>
            
            {/* Generate Button */}
            <Button 
              onClick={generatePDF} 
              disabled={generating || selectedPlans.length < 2 || !clientName} 
              className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold px-6 py-2.5 rounded-full shadow-[0_0_20px_rgba(251,191,36,0.3)] hover:shadow-[0_0_30px_rgba(251,191,36,0.5)] transition-all duration-300 disabled:opacity-50 disabled:shadow-none"
              data-testid="generate-pdf-btn"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
              ) : (
                <><FileDown className="h-4 w-4 mr-2" />Generate PDF</>
              )}
            </Button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center mt-6 gap-2 sm:gap-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'bg-[#D4A017]/15 border border-[#D4A017]/50 text-[#A87608]' 
                        : isCompleted 
                          ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-600'
                          : 'bg-[#EDF1F8]/50 border border-[#C9D4E6] text-[#3A4557] hover:border-[#C9D4E6]'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <StepIcon className="h-4 w-4" />
                    )}
                    <span className="text-sm font-medium hidden sm:inline">{step.name}</span>
                  </button>
                  {index < STEPS.length - 1 && (
                    <ChevronRight className="h-4 w-4 text-[#3A4557] mx-1 sm:mx-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Step 1: Client Details */}
            {currentStep === 1 && (
              <Card className="bg-white/50 border-[#C9D4E6] backdrop-blur-md rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#D4A017]/10 to-transparent p-6 border-b border-[#C9D4E6]">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-[#D4A017]/15 flex items-center justify-center">
                      <User className="h-6 w-6 text-[#A87608]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#1B2A4A]">Client Details</h2>
                      <p className="text-[#3A4557] text-sm">Enter client information for the quotation</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <Label htmlFor="clientName" className="text-[#2E3B57] text-sm font-medium">Client Name *</Label>
                    <Input 
                      id="clientName" 
                      value={clientName} 
                      onChange={(e) => setClientName(e.target.value)} 
                      placeholder="Enter client name" 
                      className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] mt-2 h-12 text-base rounded-lg focus:border-[#D4A017]/50 focus:ring-[#D4A017]/20"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="clientAge" className="text-[#2E3B57] text-sm font-medium">Age (Years)</Label>
                      <Input 
                        id="clientAge" 
                        type="number" 
                        value={clientAge} 
                        onChange={(e) => setClientAge(e.target.value)} 
                        placeholder="35" 
                        className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] mt-2 h-12 text-base rounded-lg focus:border-[#D4A017]/50 focus:ring-[#D4A017]/20"
                        data-testid="client-age-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="quoteDate" className="text-[#2E3B57] text-sm font-medium">Quote Date</Label>
                      <div className="mt-2">
                        <DatePicker
                          date={quoteDate}
                          onDateChange={(date) => setQuoteDate(date)}
                          placeholder="Select quote date"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={() => setCurrentStep(2)} 
                      disabled={!canProceed()}
                      className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-semibold px-6 rounded-full"
                    >
                      Continue to Plans <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Plan Selection */}
            {currentStep === 2 && (
              <Card className="bg-white/50 border-[#C9D4E6] backdrop-blur-md rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#EDF1F8]/10 to-transparent p-6 border-b border-[#C9D4E6]">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-[#EDF1F8]/40 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-[#2E3B57]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#1B2A4A]">Select Plans</h2>
                      <p className="text-[#3A4557] text-sm">Choose 2-4 plans to compare</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {plans.map((plan) => {
                      const isSelected = selectedPlans.includes(plan.id);
                      const selectionOrder = selectedPlans.indexOf(plan.id) + 1;
                      
                      return (
                        <div 
                          key={plan.id} 
                          onClick={() => handlePlanSelect(plan.id)}
                          className={`relative p-4 rounded-xl border cursor-pointer transition-all duration-300 ${
                            isSelected 
                              ? 'bg-[#D4A017]/10 border-[#D4A017] ring-1 ring-[#D4A017]/30 shadow-[0_0_20px_rgba(251,191,36,0.1)]' 
                              : 'bg-white/30 border-[#C9D4E6] hover:border-[#C9D4E6] hover:bg-[#EDF1F8]/30'
                          }`}
                          data-testid={`plan-select-${plan.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="h-14 w-14 rounded-xl bg-white p-2 flex items-center justify-center flex-shrink-0">
                              <img 
                                src={plan.company_logo} 
                                alt={plan.company}
                                className="h-full w-full object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="hidden h-full w-full items-center justify-center text-[#0D1A3A] font-bold text-xs text-center">
                                {plan.company.split(' ')[0]}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[#1B2A4A] font-semibold truncate">{plan.name}</p>
                              <p className="text-[#3A4557] text-sm truncate">{plan.company}</p>
                            </div>
                            {isSelected && (
                              <div className="h-8 w-8 rounded-full bg-[#D4A017] flex items-center justify-center text-[#0D1A3A] font-bold text-sm flex-shrink-0">
                                {selectionOrder}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-center justify-between pt-6 mt-6 border-t border-[#C9D4E6]">
                    <Button 
                      variant="ghost" 
                      onClick={() => setCurrentStep(1)}
                      className="text-[#3A4557] hover:text-[#1B2A4A]"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <div className="flex items-center gap-4">
                      <span className="text-[#3A4557] text-sm">Selected: {selectedPlans.length}/4</span>
                      <Button 
                        onClick={() => setCurrentStep(3)} 
                        disabled={!canProceed()}
                        className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-semibold px-6 rounded-full"
                      >
                        Continue <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Riders & Premiums */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {selectedPlans.map((planId) => {
                  const plan = getSelectedPlanData(planId);
                  if (!plan) return null;
                  
                  return (
                    <Card key={planId} className="bg-white/50 border-[#C9D4E6] backdrop-blur-md rounded-2xl overflow-hidden">
                      <div className="bg-gradient-to-r from-[#EDF1F8]/10 to-transparent p-5 border-b border-[#C9D4E6]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-xl bg-white p-2 flex items-center justify-center">
                              <img 
                                src={plan.company_logo} 
                                alt={plan.company}
                                className="h-full w-full object-contain"
                              />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-[#1B2A4A]">{plan.name}</h3>
                              <p className="text-[#3A4557] text-sm">{plan.company}</p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handlePlanSelect(planId)} 
                            className="text-[#3A4557] hover:text-red-600 hover:bg-red-500/10"
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                      <CardContent className="p-5 space-y-5">
                        {/* Premium Inputs */}
                        <div>
                          <Label className="text-[#2E3B57] text-sm font-medium mb-3 block">
                            Premium Amounts (₹)
                          </Label>
                          <div className="grid grid-cols-3 gap-4">
                            {["25L", "50L", "1Cr"].map((tier) => (
                              <div key={tier}>
                                <Label className="text-[#3A4557] text-xs">{tier} SI</Label>
                                <Input 
                                  type="text" 
                                  value={planPremiums[planId]?.[tier] || ""} 
                                  onChange={(e) => handlePremiumChange(planId, tier, e.target.value)} 
                                  placeholder="15,000" 
                                  className="bg-white/70 border-[#C9D4E6] text-[#1B2A4A] mt-1 h-10 rounded-lg focus:border-[#D4A017]/50"
                                  data-testid={`premium-${planId}-${tier}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Rider Selection */}
                        <div>
                          <Label className="text-[#2E3B57] text-sm font-medium mb-3 block">
                            Select Riders <span className="text-[#3A4557] font-normal">(descriptions appear in PDF)</span>
                          </Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[200px] overflow-y-auto pr-2">
                            {(plan.riders || []).map((rider) => {
                              const isRiderSelected = (selectedRiders[planId] || []).includes(rider.id);
                              
                              return (
                                <div 
                                  key={rider.id} 
                                  onClick={() => handleRiderToggle(planId, rider.id)}
                                  className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                                    isRiderSelected 
                                      ? 'bg-emerald-500/10 border-emerald-500/50' 
                                      : 'bg-white/30 border-[#C9D4E6] hover:border-[#C9D4E6]'
                                  }`}
                                  data-testid={`rider-${planId}-${rider.id}`}
                                >
                                  <div className="flex items-start gap-3">
                                    <Checkbox 
                                      checked={isRiderSelected} 
                                      className="border-[#C9D4E6] mt-0.5 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" 
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[#1B2A4A] text-sm font-medium">{rider.name}</p>
                                      <p className="text-[#3A4557] text-xs mt-0.5 line-clamp-2">{rider.description}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                <div className="flex items-center justify-between pt-4">
                  <Button 
                    variant="ghost" 
                    onClick={() => setCurrentStep(2)}
                    className="text-[#3A4557] hover:text-[#1B2A4A]"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  <Button 
                    onClick={() => setCurrentStep(4)}
                    className="bg-[#D4A017] hover:bg-[#D4A017] text-[#0D1A3A] font-semibold px-6 rounded-full"
                  >
                    Review & Generate <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {currentStep === 4 && (
              <Card className="bg-white/50 border-[#C9D4E6] backdrop-blur-md rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500/10 to-transparent p-6 border-b border-[#C9D4E6]">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <BadgeCheck className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#1B2A4A]">Review & Share</h2>
                      <p className="text-[#3A4557] text-sm">Create shareable link or download PDF</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6 space-y-6">
                  {/* Client Summary */}
                  <div className="p-4 rounded-xl bg-[#EDF1F8]/50 border border-[#C9D4E6]">
                    <h4 className="text-[#A87608] font-semibold text-sm mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" /> Client Information
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-[#3A4557]">Name</p>
                        <p className="text-[#1B2A4A] font-medium">{clientName}</p>
                      </div>
                      <div>
                        <p className="text-[#3A4557]">Age</p>
                        <p className="text-[#1B2A4A] font-medium">{clientAge || "Not specified"}</p>
                      </div>
                      <div>
                        <p className="text-[#3A4557]">Date</p>
                        <p className="text-[#1B2A4A] font-medium">{formatDate(quoteDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Plans Summary */}
                  <div className="p-4 rounded-xl bg-[#EDF1F8]/50 border border-[#C9D4E6]">
                    <h4 className="text-[#A87608] font-semibold text-sm mb-3 flex items-center gap-2">
                      <Shield className="h-4 w-4" /> Selected Plans ({selectedPlans.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedPlans.map(planId => {
                        const plan = getSelectedPlanData(planId);
                        const riderCount = (selectedRiders[planId] || []).length;
                        if (!plan) return null;
                        
                        return (
                          <div key={planId} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-white p-1.5">
                                <img src={plan.company_logo} alt={plan.company} className="h-full w-full object-contain" />
                              </div>
                              <div>
                                <p className="text-[#1B2A4A] font-medium text-sm">{plan.name}</p>
                                <p className="text-[#3A4557] text-xs">{riderCount} rider(s) selected</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Share Link Section */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#EDF1F8]/10 to-transparent border border-blue-500/30">
                    <div className="flex items-start gap-4">
                      <Link2 className="h-8 w-8 text-[#2E3B57] flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-[#1B2A4A] font-semibold">Share with Client</h4>
                        <p className="text-[#3A4557] text-sm mt-1">
                          Create a shareable link that your client can open to view the comparison online. They can also download the PDF from there.
                        </p>
                        
                        {shareLink ? (
                          <div className="mt-4 space-y-3">
                            <div className="flex items-center gap-2 p-3 bg-white/50 rounded-lg border border-[#C9D4E6]">
                              <input 
                                type="text" 
                                value={shareLink} 
                                readOnly 
                                className="flex-1 bg-transparent text-[#2E3B57] text-sm outline-none truncate"
                              />
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={copyShareLink}
                                className="text-[#3A4557] hover:text-[#1B2A4A] flex-shrink-0"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={copyShareLink}
                                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A]"
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                Copy Link
                              </Button>
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(shareLink, '_blank')}
                                className="border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A]"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open Link
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button 
                            onClick={createShareableLink}
                            disabled={creatingLink}
                            className="mt-4 bg-blue-500 hover:bg-[#EDF1F8] text-white font-semibold px-6 rounded-full"
                          >
                            {creatingLink ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating Link...</>
                            ) : (
                              <><Link2 className="h-4 w-4 mr-2" />Create Shareable Link</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* PDF Option */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[#D4A017]/10 to-transparent border border-[#D4A017]/30">
                    <div className="flex items-start gap-4">
                      <FileDown className="h-8 w-8 text-[#A87608] flex-shrink-0" />
                      <div>
                        <h4 className="text-[#1B2A4A] font-semibold">Download as PDF</h4>
                        <p className="text-[#3A4557] text-sm mt-1">
                          Generate and download a PDF for offline sharing or printing.
                        </p>
                        <ul className="text-[#3A4557] text-xs mt-2 space-y-1">
                          <li className="flex items-center gap-1.5">
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                            Company logos and plan details
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                            IRDAI claim experience data
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                            Selected riders with descriptions
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4">
                    <Button 
                      variant="ghost" 
                      onClick={() => setCurrentStep(3)}
                      className="text-[#3A4557] hover:text-[#1B2A4A]"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button 
                      onClick={generatePDF}
                      disabled={generating}
                      className="bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold px-8 py-3 rounded-full shadow-[0_0_20px_rgba(251,191,36,0.3)] hover:shadow-[0_0_30px_rgba(251,191,36,0.5)] transition-all duration-300"
                    >
                      {generating ? (
                        <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><FileDown className="h-5 w-5 mr-2" />Download PDF</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-36">
              <Card className="bg-white/50 border-[#C9D4E6] backdrop-blur-md rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-[#C9D4E6]">
                  <h3 className="text-lg font-bold text-[#1B2A4A] flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[#A87608]" />
                    Quote Summary
                  </h3>
                </div>
                <CardContent className="p-5 space-y-4">
                  {/* Client */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#3A4557]">Client</span>
                    <span className="text-[#1B2A4A] font-medium">{clientName || "Not set"}</span>
                  </div>
                  
                  {/* Plans Count */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#3A4557]">Plans Selected</span>
                    <span className={`font-medium ${selectedPlans.length >= 2 ? 'text-emerald-600' : 'text-[#A87608]'}`}>
                      {selectedPlans.length}/4
                    </span>
                  </div>

                  {/* Selected Plans List */}
                  {selectedPlans.length > 0 && (
                    <div className="pt-4 border-t border-[#C9D4E6] space-y-3">
                      {selectedPlans.map((planId, index) => {
                        const plan = getSelectedPlanData(planId);
                        if (!plan) return null;
                        
                        return (
                          <div key={planId} className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-white p-1 flex-shrink-0">
                              <img src={plan.company_logo} alt={plan.company} className="h-full w-full object-contain" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[#1B2A4A] text-sm font-medium truncate">{plan.name}</p>
                            </div>
                            <span className="text-[#A87608] text-xs font-bold">{index + 1}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Generate Button */}
                  <div className="pt-4 border-t border-[#C9D4E6]">
                    <Button 
                      onClick={generatePDF}
                      disabled={generating || selectedPlans.length < 2 || !clientName}
                      className="w-full bg-gradient-to-r from-[#D4A017] via-yellow-500 to-[#D9A82C] text-[#0D1A3A] font-bold py-3 rounded-full shadow-[0_0_15px_rgba(251,191,36,0.2)] hover:shadow-[0_0_25px_rgba(251,191,36,0.4)] transition-all duration-300 disabled:opacity-50"
                    >
                      {generating ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><FileDown className="h-4 w-4 mr-2" />Generate PDF</>
                      )}
                    </Button>
                    
                    {selectedPlans.length < 2 && (
                      <p className="text-[#A87608]/80 text-xs text-center mt-3">
                        Select at least 2 plans to generate
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
